
import { FLEX_CONTAINER } from "@cervval.js/client-html5-core/src/tools/cssClasses.tools";
import { createHtmlElement } from "@cervval.js/client-html5-core/src/tools/dom.tools";
import { getLogConsole } from "@cervval.js/common/src/services/log.service";
import { includes, pushIfNewAndDefined } from "@cervval.js/common/src/tools/collection.tools";
import { toPx } from "@cervval.js/common/src/tools/number.tools";
import { compare, objectEntries, objectValues } from "@cervval.js/common/src/tools/object.tools";
import { FilterRegexMode, REGEX_FILTER_DEFAULT_DELIMITER, REGEX_FILTER_DEFAULT_MULTI_WILDCARD, REGEX_FILTER_DEFAULT_SINGLE_WILDCARD, filterString } from "@cervval.js/common/src/tools/string.tools";
import { AdvancedFilter } from "@cervval.js/common/src/ui/advancedFilters/advancedFilter";
import { ADVANCED_FILTER_DEFAULT_CONDITIONS, AdvancedFilterCondition, andCondition } from "@cervval.js/common/src/ui/advancedFilters/advancedFilterCondition";
import { AdvancedFilterOperator, DEFAULT_ADVANCED_FILTER_OPERATOR_LIST } from "@cervval.js/common/src/ui/advancedFilters/advancedFilterOperator";
import { ColumnUid } from "@cervval.js/common/src/ui/columnConfiguration/columnConfiguration";
import { anyReallyAny } from "@cervval.js/common/tools";
import { CjsIconFa } from "../icon/iconFa";
import { getIconFaName } from "../icon/iconFa.tools";
import { IconFA6 } from "../icon/tools/fontAwesome6IconList.tools";
import { CjsInput } from "../inputs/input";
import { CjsColumnConfigurator } from "./columnConfigurator/columnConfigurator";
import { CjsColumnExcelLikeSortFilter, ColumnExcelLikeSortFilterOptions, ExcelLikeFilter } from "./columnExcelLikeSortFilter/columnExcelLikeSortFilter";
import { CjsSmartTable, RenderCacheData, SmartTableCell, SmartTableClearConfigurationParams, SmartTableColumn, SmartTableRow } from "./smartTable";


/**
 * Model of a filter of SmartTable
 */
export interface SmartTableFilter<ItemData> {

    /** Column filtered */
    column: SmartTableColumn<ItemData>;

    /**
     * Value of filter
     */
    value: string;
}

/** On input in filter field */
export type SmartTableOnFilterEvent<ItemData> = {

    /**
     * Filter currently applied to smartTable
     */
    filter: SmartTableFilter<ItemData>
};


export interface SmartTableFilterOptions {
    /** Options for advanced filters */
    advancedFiltersOptions?: {
        /** TODO Comment */
        advancedFilters?: AdvancedFilter<anyReallyAny>[],
        /** TODO Comment */
        operators?: AdvancedFilterOperator[],
        /** TODO Comment */
        conditions?: AdvancedFilterCondition[]
    };

    /** Enable clear button on filters in each column header */
    filtersEnableClearButton?: boolean;
}

export const defaultSmartTableFilterOptions: SmartTableFilterOptions = {
    advancedFiltersOptions: {
        operators: [...DEFAULT_ADVANCED_FILTER_OPERATOR_LIST],
        conditions: [...ADVANCED_FILTER_DEFAULT_CONDITIONS]
    }

};

/** Merge all filters to be applied to a column of smartTable */
interface SmartTableMergedFilters<ItemData> {
    /** Filter defined in header inputValue filter */
    tableFilter?: SmartTableFilter<ItemData>;
    /** Filter defined in excelLikeSortFilter component */
    excelLikeFilter?: ExcelLikeFilter<ItemData>;
}

export class SmartTableFilterHelper<ItemData> {

    /** Filters of table */
    public filters: SmartTableFilter<ItemData>[] = [];

    /** Getter for ExcelLikeSortFilters of table */
    public get excelLikeFilters(): Record<string, ExcelLikeFilter<ItemData>> {
        return this.excelLikeSortFilter?.filters ?? {};
    }

    /** Operators of filters */
    public get operators(): AdvancedFilterOperator[] {
        return this._smartTable.options.advancedFiltersOptions?.operators ?? [];
    }

    /** Conditions of filters */
    public get conditions(): AdvancedFilterCondition[] {
        return this._smartTable.options.advancedFiltersOptions?.conditions ?? [];
    }

    /** Get advanced filters */
    public get advancedFilters(): AdvancedFilter<anyReallyAny>[] {
        return this._smartTable.options.advancedFiltersOptions?.advancedFilters ?? [];
    }

    /** Set advanced filters of smartTable */
    public set advancedFilters(advancedFilters: AdvancedFilter<anyReallyAny>[]) {
        this._smartTable.options.setWithoutRefresh({ advancedFiltersOptions: { ...this._smartTable.options.advancedFiltersOptions, advancedFilters } });
        this._smartTable.renderHeader({});
        this.filterTable({ refresh: true, emit: true });
    }

    /** Excel like sort filter component */
    public excelLikeSortFilter?: CjsColumnExcelLikeSortFilter<ItemData>;

    /** 
     * Column configurator of smartTable, defined if installed, 
     */
    protected _columnConfigurator?: CjsColumnConfigurator<ItemData> | undefined;

    /** 
     * Get column configurator of smartTable, defined if installed
     * Will point to excelLikeSortFilter columnConfigurator if excelLikeSortFilter is installed
     */
    public get columnConfigurator(): CjsColumnConfigurator<ItemData> | undefined {
        if (this.excelLikeSortFilter) {
            this._columnConfigurator = this.excelLikeSortFilter.columnConfigurator;
        }
        return this._columnConfigurator;
    }

    constructor(private _smartTable: CjsSmartTable<ItemData>) { }

    // #Region Filters
    /**
     * Hide/Show rows in cell text content contains characters from search filter.
     * Optional boolean if we want to dispatch a ON_FILTER_EVENT (default true)
     */
    public filterTable(p?: {
        renderCacheData?: RenderCacheData,
        filter?: SmartTableFilter<ItemData>,// TODO comment usage of filter parameter
        refresh?: boolean,
        reason?: string,
        emit?: boolean
    }): void {

        const renderCacheData = p?.renderCacheData ?? {};
        const table = this._smartTable;

        // Check if usefull to apply any filter
        if (!table.options.columns.length && !table.tableRows.length) { return; }

        // Work on displayed rows from excel like filter component if exists to keep the initial filter
        // Else show every row before first filter (needed when delete characters in filter)
        for (const r of table.tableRows) {
            r.displayed = true;
        }

        // Clean info on descendant filtered
        // info will be rebuilt after the updated filters have been applied here via function updateFlatListAttributeInAncestors
        table.hierarchyHelper.cleanFlatList();


        // Get all filters set in table for a given column uid
        const filtersByColumn: Record<ColumnUid, SmartTableMergedFilters<ItemData>> = {};
        for (const tableFilter of this.filters) {
            filtersByColumn[tableFilter.column.uid] = { tableFilter };
        }
        for (const [columnUid, excelLikeFilter] of objectEntries(this.excelLikeFilters)) {
            filtersByColumn[columnUid] = {
                ...filtersByColumn[columnUid],
                excelLikeFilter
            };
        }


        // If first condition for custom filters should be taken into account
        let ignoreFirstCondition = true;

        // Loop every column filters
        for (const filters of objectValues(filtersByColumn)) {

            // TODO prepare AdvancedFilter object before iteration

            // Avoid useless loop if no filterValue is fedDon't hide row if only tableFilter is defined with empty inputValue (=no filter)
            if (filters.tableFilter && !filters.tableFilter.value && !filters.excelLikeFilter) { continue; }

            // Used by advancedFilters, not by smartTable column filters
            if (ignoreFirstCondition && filters.tableFilter && this.isAdvancedFiltered(filters.tableFilter.column.propertyConfig?.propertyName ?? "")) {
                const inputValue = filters.tableFilter.value.toLocaleLowerCase();
                if (inputValue.length === 0) { continue; }
                else { ignoreFirstCondition = false; }
            }

            // Check for every row if should be displayed
            for (const row of table.tableRows) {
                // no need to test, already false
                if (!row.displayed) { continue; }

                // Check if input value is given
                this.refreshRowDisplayFromCellValue({ table, filters, row, renderCacheData });
            }
        }

        // Custom filters are applied after the list of regular filters
        // this ensures regular filters are applied the same with or without custom filters
        // see custom filter editor showroom for examples
        const hasAdvancedFiltered = this.hasAdvancedFiltered();
        if (hasAdvancedFiltered) {
            for (const row of table.tableRows) {
                const rowItemData = table.getRowItemData(row);
                row.displayed = this.applyAdvancedFilters(row, rowItemData, row.displayed, ignoreFirstCondition, renderCacheData);
            }
        }

        // If row has been displayed ans is collapsed, set attribute isInFlatList of ancestors to true 
        table.hierarchyHelper.updateFlatListAttributeInAncestors();

        // Set rows to display before refresh (because smart select needs it to determine row count)
        table.updateDisplayableRows();

        // Reset lead selection
        this._smartTable.getSelectionModel().leadSelectionRow = "";

        if (p?.emit !== false) {
            if (p?.filter != null) {
                // emit before refresh so that smart select can adjust its height depending on row count after filter
                table.filterEvent.emit({
                    filter: p.filter
                });
            } else if (hasAdvancedFiltered) {
                // if filter is not defined, change comes from a AdvancedFilterEvent
                table.advancedFilterEvent.emit();
            }
        }

        if (p?.refresh !== false) {
            table.refresh(p?.reason ?? "filter", { renderCacheData });
        }
    }

    /**
     * Refresh row.displayed value from cell value of given column
     * @returns row.displayed
     */
    public refreshRowDisplayFromCellValue(params: {
        table: CjsSmartTable<ItemData>,
        row: SmartTableRow<ItemData>,
        filters: SmartTableMergedFilters<ItemData>,
        renderCacheData: RenderCacheData | undefined
    }): boolean {

        const { table, row, filters, renderCacheData } = params;
        const tableFilter = filters.tableFilter;
        const inputValue = tableFilter?.value.toLocaleLowerCase();

        // First, apply table header filter input
        if (tableFilter && inputValue) {
            const column = tableFilter.column;
            const filterValue = tableFilter.value;
            const rowItemData = table.getRowItemData(row);
            let cell: SmartTableCell<ItemData> | undefined = table.getCellByRowAndColumn(row.uid, column.uid, renderCacheData);
            if (!cell) {
                // Create custom cell
                cell = table.buildCell(row, column, renderCacheData, { doNotRegisterCellInCache: true });
            }

            // Apply smartTable filter first
            if (column.filterComparator) {
                // Get custom cell value, use getDisplayString if defined
                if (!column.filterComparator({ item: rowItemData, itemValue: cell.value, filterValue }, renderCacheData)) {
                    row.displayed = false;
                    return row.displayed;
                }
            } else if (column.getDisplayString) {
                const cellDisplayString = column.getDisplayString(cell, renderCacheData);
                if (!filterString(cellDisplayString, inputValue, column.defaultFilterOptions)) {
                    row.displayed = false;
                    return row.displayed;
                }
            } else {
                // Get custom cell value, use getDisplayString if defined
                const cellValue = cell.value;
                if (cellValue != null && !(cellValue instanceof HTMLElement)) {
                    // Get value parsed with with propertyConfig first
                    const parsedCellValue = table.cellPropertyConfigTypeHandler(cell) ?? String(cellValue);

                    if (!filterString(parsedCellValue, inputValue, column.defaultFilterOptions)) {
                        row.displayed = false;
                        return row.displayed;
                    }
                } else {
                    row.displayed = false;
                    return row.displayed;
                }
            }

        }

        // Then, if excelLike filter is defined for this column, restrict row display to selectedValues of filter
        const excelLikeFilter = filters.excelLikeFilter;
        if (excelLikeFilter && this.excelLikeSortFilter) {
            let cell: SmartTableCell<ItemData> | undefined = table.getCellByRowAndColumn(row.uid, excelLikeFilter.column.uid, renderCacheData);
            if (!cell) {
                // Create custom cell
                cell = table.buildCell(row, excelLikeFilter.column, renderCacheData, { doNotRegisterCellInCache: true });
            }

            let columnFilterValid = true;

            const cellDisplayString = this.excelLikeSortFilter.getDisplayStringValue(cell, renderCacheData);
            const colSelectedValues = excelLikeFilter.selectedValues;

            // If value is an array, check that at least one selected value is contained in the value array
            if (Array.isArray(cell.value)) {
                let cellDisplayStringValues = cellDisplayString?.split(",")?.filter(v => Boolean(v));
                if (!cellDisplayStringValues?.length) {
                    cellDisplayStringValues = [this.excelLikeSortFilter.i18n.filterEmptyNullUndefinedValue];
                }
                columnFilterValid = cellDisplayStringValues.some(stringValue => includes(colSelectedValues, stringValue.trim()));
            }
            // Flat values
            else {
                let filterEmptyNullUndefinedValueFound = false;
                let cellDisplayStringFound = false;
                for (const selectedStringValue of colSelectedValues) {
                    // Check null/undefined/empty values and if Blanks value has been checked
                    if (!cellDisplayString && selectedStringValue === this.excelLikeSortFilter.i18n.filterEmptyNullUndefinedValue) {
                        filterEmptyNullUndefinedValueFound = true;
                        break;
                    }

                    // Display row if cell display string is contained in selected values
                    if (selectedStringValue === cellDisplayString) {
                        cellDisplayStringFound = true;
                    }

                    // If both conditions match, useless to continue loop
                    if (cellDisplayStringFound && filterEmptyNullUndefinedValueFound) { break; }
                }

                // Assign row displayed
                if (filterEmptyNullUndefinedValueFound) {
                    columnFilterValid = true;
                } else {
                    columnFilterValid = cellDisplayStringFound;
                }
            }

            row.displayed = columnFilterValid;
            if (!columnFilterValid) {
                return false;
            }
        }

        row.displayed = true;
        return true;
    }

    /** Get smartTable filters */
    public getNotEmptySmartTableFilters(): SmartTableFilter<ItemData>[] {
        return this.filters.filter(filter => filter.value !== "");
    }

    /** Set smartTable filters */
    public setSmartTableFilters(filters: SmartTableFilter<ItemData>[], clear?: SmartTableClearConfigurationParams): void {

        const table = this._smartTable;

        // Clear filters and/or sort if required
        table.clearConfiguration({ ...clear });
        // NB columns with no propertyConfig are not handled by AdvancedFilterEditor
        // if such a column has a regular filter, it will not change via this method
        // if only a set of regular filters have been sent to the AdvancedFilterEditor, only this filters will be return through this function
        for (const f of filters) {
            const filter = this.filters.find(smartTableFilter => smartTableFilter.column.uid === f.column.uid);

            if (filter != null) {
                filter.value = f.value;
            } else {
                this.filters.push(f);
            }
        }
        const renderCacheData: RenderCacheData = {};
        table.renderHeader(renderCacheData); // update header with filter values from update smartTableFilter list
        this.filterTable({ renderCacheData });
    }

    /**
     * Callback triggered when input on search column filters
     */
    public onFilterEnterPressed(column: SmartTableColumn<ItemData>, searchInput: CjsInput<string>, e: KeyboardEvent): void {
        const filter = this.filters.find(f => f.column.uid === column.uid);
        const table = this._smartTable;

        if (!filter) {
            getLogConsole().error("Impossible to find column filter for column: " + column.uid);
            return;
        }
        filter.value = searchInput.value ?? "";
        table.filterEnterKeyEvent.emit({ filter });
    }

    /**
     * Callback triggered when input on search column filters
     */
    public onFilterInput(column: SmartTableColumn<ItemData>, searchInput: CjsInput<string>, e: Event): void {

        const filter = this.filters.find(f => f.column.uid === column.uid);

        if (!filter) {
            getLogConsole().error("Impossible to find column filter for column: " + column.uid);
        } else {
            filter.value = searchInput.value ?? "";
            this.filterTable({ filter, renderCacheData: {} });// Create new renderCacheData for filter call, table will compute all cells values => common renderCache
        }
    }


    /**
     * Check if column should be filtered (from options)
     */
    public columnIsFilterable(column: SmartTableColumn<ItemData>): boolean {
        if (column.filterDisabled) { return false; }
        const tableOptions = this._smartTable.options;
        return !tableOptions.disableFilter && (!tableOptions.columnsToDisableFilter || !tableOptions.columnsToDisableFilter?.includes(column.uid));
    }

    /**
     * Get filter placeholder based on column parameters.
     */
    public getColumnFilterPlaceholder(column: SmartTableColumn<ItemData>): string {
        const table = this._smartTable;
        let placeholder: string = table.i18n.searchInputLabel;
        const regexParams = column.defaultFilterOptions?.regularExpression;
        if (regexParams != null) {
            // If regex mode is enabled, display useful information for the user.
            placeholder += " ";
            if (regexParams.enable === FilterRegexMode.RAW) {
                placeholder += table.i18n.searchInputLabelJSRegex;
            } else {
                placeholder += `${regexParams.singleWildcard ?? REGEX_FILTER_DEFAULT_SINGLE_WILDCARD}${regexParams.multiWildcard ?? REGEX_FILTER_DEFAULT_MULTI_WILDCARD}`;
                if (regexParams.enable === FilterRegexMode.BOTH) {
                    placeholder += regexParams.delimiter ?? REGEX_FILTER_DEFAULT_DELIMITER;
                }
            }
        }
        return placeholder;
    }

    // #EndRegion Filters
    // #Region Advanced filters

    /** @returns true if there's a custom filter for the propertyName */
    public isAdvancedFiltered(propertyName: string | undefined): boolean {
        if (this.advancedFilters && propertyName != null) {
            if (this.advancedFilters.some(f => f.attributePropertyName === propertyName)) {
                return true;
            }
        }
        return false;

    }

    /** Get icon if excelLikeSortFilter filter is defined on column */
    public getFilterIcon(columnUid: ColumnUid): HTMLSpanElement | undefined {
        const columnExcelLikeSortFilterOrder = this.excelLikeFilters[columnUid]?.order;
        if (columnExcelLikeSortFilterOrder == null) { return undefined; }
        const spanIcon = createHtmlElement("span", {
            cssClass: [FLEX_CONTAINER, "filtered-column-icon"],
            style: { marginRight: toPx(12) },
            innerHTML: [
                columnExcelLikeSortFilterOrder != null ? String(columnExcelLikeSortFilterOrder) : "",
                new CjsIconFa({
                    name: getIconFaName(IconFA6.FILTER)
                })
            ]
        });

        spanIcon.addEventListener("click", e => {
            const column = this._smartTable.options.columns.find(c => c.uid === columnUid);
            if (column) {
                this.excelLikeSortFilter?.show(e, column);
            }
        });

        return spanIcon;
    }

    /** @returns true if at least one custom filter is defined */
    public hasAdvancedFiltered(): boolean {
        return Boolean(this.advancedFilters.length);
    }

    /** Instantiate excel like sort filter component */
    public refreshExcelLikeSortFilter(options?: Partial<ColumnExcelLikeSortFilterOptions<ItemData>>): void {
        if (this.excelLikeSortFilter) { return; }
        this.excelLikeSortFilter = new CjsColumnExcelLikeSortFilter({ ...options, smartTable: this._smartTable });
    }

    /** Instantiate columnConfigurator component */
    public refreshColumnConfigurator(): void {
        if (this.columnConfigurator) { return; }
        this._columnConfigurator = new CjsColumnConfigurator({ smartTable: this._smartTable }, this._smartTable.options.columnConfigurator?.options);
    }

    /** @returns true if there's filter applied to the column with excel like sort filter component */
    public isExcelLikeFiltered(colUid: string | undefined): boolean {
        if (!colUid) { return false; }
        return Boolean(this.excelLikeSortFilter?.isColumnFiltered(colUid));
    }


    /** 
     * Apply custom filters to a row 
     * very ugly, build priorityConditionAndValue for each row
     * TODO create a function to prepare this computation before all the filters are applied
     * AdvancedFilters are applied in the order of their priority
     * if three customs filters OR operator1 AND operator2 OR operator3, result will be shouldDsiplayRow (regular filters result) OR op1 AND op2 OR op3
     * @param row row of the smartTable
     * @param rowItemData ItemData corresponding to the row
     * @param shouldDisplayRow result of the regular filters 
     * @param ignoreFirstCondition true if first AdvancedFilter and regular filter list is empty
     */
    public applyAdvancedFilters(row: SmartTableRow<ItemData>, rowItemData: ItemData | null, shouldDisplayRow: boolean, ignoreFirstCondition: boolean, renderCacheData: RenderCacheData): boolean {
        // Apply all AdvancedFilters with priorities
        let first = true;
        const priorities: number[] = [];
        let priorityConditionAndValues: { condition: AdvancedFilterCondition, value: anyReallyAny }[] = [];

        // builds a list of values with their associated conditions 
        for (const f of this.advancedFilters) {
            const column = this._smartTable.options.columns.find(c => c.propertyConfig?.propertyName === f.attributePropertyName);
            if (column != null) {
                let condition = this.conditions.find(cond => cond.uid === f.conditionUid);
                const operator = this.operators.find(op => op.uid === f.operatorUid);
                if (condition != null && operator != null) {
                    const cellValue = this._smartTable.getCellByRowAndColumn(row.uid, column.uid)?.value;

                    // if first value and regular list is empty => first condition is changed to AND and row.displayed will be the result of the custom filters 
                    if (first && ignoreFirstCondition) {
                        condition = andCondition;
                        shouldDisplayRow = true;
                    }

                    priorityConditionAndValues.push({
                        condition,
                        value: operator.comparator(cellValue, f.value)
                    });
                    pushIfNewAndDefined(priorities, condition.priority);
                }
            }
            first = false;
        }

        // Sort priorities
        priorities.sort(compare);

        // applies condtions in the order of the priorities
        // each loop create a reduced list of condition and values
        // first list of values and conditions : [AND v1, OR v2, AND v3] => 
        // 1) shouldDisplayRow = (v1 AND shouldDisplayRow), seocnd list of conditions : [ OR (v2 AND v3)] 
        // 2) shouldDisplayRow = shoudlDisplayRow OR (v2 AND v3)
        for (const p of priorities) {
            const newPriorityConditionAndValues: { condition: AdvancedFilterCondition, value: anyReallyAny }[] = [];
            for (let i = 0; i < priorityConditionAndValues.length; i++) {
                const pcv = priorityConditionAndValues[i];
                if (pcv.condition.priority === p) {
                    // previousValue is either the value that has just been pushed in the list being built
                    const previousValue = !newPriorityConditionAndValues.length ? shouldDisplayRow :
                        newPriorityConditionAndValues[newPriorityConditionAndValues.length - 1].value;
                    const newValue = pcv.condition.comparator(pcv.value, previousValue);

                    // updated value is either the shouldDisplayRow or the condition/value lastly pushed in the new list of condition/value
                    if (newPriorityConditionAndValues.length) {
                        newPriorityConditionAndValues[newPriorityConditionAndValues.length - 1].value = newValue;
                    } else {
                        shouldDisplayRow = newValue;
                    }
                } else {
                    newPriorityConditionAndValues.push(pcv);
                }
            }
            priorityConditionAndValues = newPriorityConditionAndValues;
        }
        return shouldDisplayRow;
    }
}